/**
 * ConsoleVariableHandler - integrates console variables with the console command system.
 * Automatically registers console commands for all registered console variables.
 */

import { ConsoleCommand } from './ConsoleManager.js';
import { CVarManager } from './ConsoleVariableManager.js';

export class ConsoleVariableHandler {
  constructor() {
    // Register built-in console variable commands
  }

  @ConsoleCommand('cvar.list', 'List all registered console variables')
  command_cvar_list(): string {
    const variables = CVarManager.getAllVariables();
    if (variables.length === 0) {
      return 'No console variables registered';
    }

    let output = `Registered console variables (${variables.length}):\n`;
    for (const variable of variables) {
      const range = variable.min !== undefined || variable.max !== undefined
        ? ` [${variable.min ?? '-∞'}, ${variable.max ?? '∞'}]`
        : '';
      output += `  ${variable.name} = ${variable.get()} (default: ${variable.defaultValue})${range}`;
      if (variable.description) {
        output += ` - ${variable.description}`;
      }
      output += '\n';
    }
    return output.trim();
  }

  @ConsoleCommand('cvar.reset', 'Reset a console variable to its default value')
  command_cvar_reset(variableName?: string): string {
    if (!variableName) {
      return 'Usage: cvar_reset <variable_name>';
    }

    if (!CVarManager.hasVariable(variableName)) {
      return `Unknown console variable: ${variableName}`;
    }

    const variable = CVarManager.getVariable(variableName);
    const success = CVarManager.resetVariable(variableName);

    if (success && variable) {
      return `${variable.name} reset to ${variable.defaultValue}`;
    } else {
      return `Failed to reset ${variableName}`;
    }
  }

  @ConsoleCommand('cvar.reset_all', 'Reset all console variables to their default values')
  command_cvar_reset_all(): string {
    const variables = CVarManager.getAllVariables();
    let resetCount = 0;

    for (const variable of variables) {
      if (CVarManager.resetVariable(variable.name)) {
        resetCount++;
      }
    }

    return `Reset ${resetCount} console variables to their default values`;
  }

  @ConsoleCommand('cvar.find', 'Find console variables by name pattern')
  command_cvar_find(pattern?: string): string {
    if (!pattern) {
      return 'Usage: cvar_find <pattern>';
    }

    const variables = CVarManager.getAllVariables();
    const regex = new RegExp(pattern, 'i');
    const matches = variables.filter(v => regex.test(v.name));

    if (matches.length === 0) {
      return `No console variables found matching pattern: ${pattern}`;
    }

    let output = `Console variables matching "${pattern}" (${matches.length}):\n`;
    for (const variable of matches) {
      output += `  ${variable.name} = ${variable.get()}`;
      if (variable.description) {
        output += ` - ${variable.description}`;
      }
      output += '\n';
    }
    return output.trim();
  }

  /**
   * Handle dynamic console variable commands.
   * Called by the console system for unknown commands to check for registered console variables.
   */
  handleDynamicCommand(commandName: string, args: string[]): string | null {
    if (CVarManager.hasVariable(commandName)) {
      return CVarManager.executeVariableCommand(commandName, args);
    }
    return null;
  }
}
